import React, { useState } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Container, Card, Form, Row, Col, Table, Button, Modal, Badge } from "react-bootstrap";
import { Select, DatePicker } from 'antd';
import { FilterOutlined, PlayCircleOutlined, PauseCircleOutlined, EyeOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import FormItem from '@/common/FormItem/FormItem';
import PageTitle from "@/common/PageTitle/PageTitle";
import PaginationRow from "@/components/PaginationRow/PaginationRow";
import styles from "./message-requests.module.scss";

interface MessageRequest {
  id: string;
  clientName: string;
  locationName: string;
  messageText: string;
  priority: 'urgent' | 'standard';
  status: 'pending' | 'approved' | 'rejected' | 'scheduled';
  requestedAt: string;
  requestedBy: string;
  duration: number; // in seconds
  audioFile?: string;
  notes?: string;
}

const MessageRequests: React.FC = () => {
  // Filter state
  const [filters, setFilters] = useState({
    priority: undefined as string | undefined,
    status: undefined as string | undefined,
    requestedDate: undefined as string | undefined,
  });
  // Reset counter for forcing re-render of components
  const [resetCounter, setResetCounter] = useState(0);
  const [page, setPage] = useState(1);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<MessageRequest | null>(null);

  // Mock data
  const messageRequests: MessageRequest[] = [
    {
      id: 'MR-001',
      clientName: 'Downtown Coffee Co.',
      locationName: 'Main Street Location',
      messageText: 'Welcome to Downtown Coffee Co! Try our new seasonal blend today.',
      priority: 'urgent',
      status: 'pending',
      requestedAt: '2024-01-20',
      requestedBy: 'Sarah Johnson',
      duration: 15,
      notes: 'Client requested this message to play during morning rush hour'
    },
    {
      id: 'MR-002',
      clientName: 'Fitness First Gym',
      locationName: 'Westside Branch',
      messageText: 'Don\'t forget to hydrate! Water station is available in the lobby.',
      priority: 'standard',
      status: 'pending',
      requestedAt: '2024-01-19',
      requestedBy: 'Mike Wilson',
      duration: 12,
      notes: 'Health and safety reminder for gym members'
    },
    {
      id: 'MR-003',
      clientName: 'Tech Solutions Inc.',
      locationName: 'Corporate Office',
      messageText: 'Lunch break reminder: Our cafeteria is open until 2 PM.',
      priority: 'urgent',
      status: 'approved',
      requestedAt: '2024-01-18',
      requestedBy: 'Lisa Brown',
      duration: 10,
      audioFile: 'lunch_reminder_001.mp3'
    },
    {
      id: 'MR-004',
      clientName: 'Retail Plus Store',
      locationName: 'Mall Location',
      messageText: 'Special sale today only! 20% off all electronics.',
      priority: 'standard',
      status: 'scheduled',
      requestedAt: '2024-01-17',
      requestedBy: 'David Lee',
      duration: 18,
      notes: 'Promotional message for weekend sale'
    },
    {
      id: 'MR-005',
      clientName: 'Medical Center',
      locationName: 'Emergency Department',
      messageText: 'Please maintain social distancing in waiting areas.',
      priority: 'urgent',
      status: 'pending',
      requestedAt: '2024-01-16',
      requestedBy: 'Dr. Smith',
      duration: 14,
      notes: 'COVID-19 safety protocol reminder'
    }
  ];

  // Generate filter options dynamically
  const filterOptions = {
    priority: ['Urgent', 'Standard'],
    status: ['Pending', 'Approved', 'Rejected', 'Scheduled']
  };

  // Filter requests based on search terms
  const filteredRequests = messageRequests.filter(request => {
    return (
      (!filters.priority || request.priority === filters.priority.toLowerCase()) &&
      (!filters.status || request.status === filters.status.toLowerCase()) &&
      (!filters.requestedDate || request.requestedAt === filters.requestedDate)
    );
  });
  
  const totalPages = 1; // Since we only have a few test requests

  // Filter change
  const handleFilterChange = (key: string, value: string | undefined) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    setFilters({ 
      priority: undefined, 
      status: undefined, 
      requestedDate: undefined 
    });
    setResetCounter(prev => prev + 1);
  };

  const handleOnClickNext = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const handleOnClickPrev = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleViewDetails = (request: MessageRequest) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  const handleApprove = (requestId: string) => {
    console.log('Approving message request:', requestId);
    // In real app, this would make an API call
  };

  const handleReject = (requestId: string) => {
    console.log('Rejecting message request:', requestId);
    // In real app, this would make an API call
  };

  const handleSchedule = (requestId: string) => {
    console.log('Scheduling message request:', requestId);
    // In real app, this would make an API call
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return <Badge bg="danger">Urgent</Badge>;
      case 'standard':
        return <Badge bg="info">Standard</Badge>;
      default:
        return <Badge bg="secondary">{priority}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge bg="warning">Pending</Badge>;
      case 'approved':
        return <Badge bg="success">Approved</Badge>;
      case 'rejected':
        return <Badge bg="danger">Rejected</Badge>;
      case 'scheduled':
        return <Badge bg="primary">Scheduled</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const formatDuration = (seconds: number) => {
    return `${seconds}s`;
  };

  return (
    <Container fluid>
      <Card>
        <Card.Body>
          <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
            <PageTitle title="Message Requests" />
            <Button 
              onClick={handleReset}
              style={{ 
                background: 'white', 
                color: '#333', 
                border: '1px solid #e0e0e0', 
                borderRadius: '6px',
                padding: '8px 16px',
                fontWeight: '500'
              }}
            >
              <FilterOutlined /> Reset All
            </Button>
          </Card.Title>
          
          <Form className={styles.form}>
            <Row>
              <Col>
                <Row className="mb-2">
                  <Col>
                    <Row>
                      <Col md={4}>
                        <FormItem
                          label="Priority"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`priority-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any priority"
                            value={filters.priority}
                            onChange={value => handleFilterChange('priority', value)}
                            options={filterOptions.priority.map(priority => ({ label: priority, value: priority }))}
                          />
                        </FormItem>
                      </Col>
                      <Col md={4}>
                        <FormItem
                          label="Status"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`status-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any status"
                            value={filters.status}
                            onChange={value => handleFilterChange('status', value)}
                            options={filterOptions.status.map(status => ({ label: status, value: status }))}
                          />
                        </FormItem>
                      </Col>
                      <Col md={4}>
                        <FormItem
                          label="Requested Date"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <DatePicker
                            key={`requestedDate-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any date"
                            value={filters.requestedDate ? new Date(filters.requestedDate) : undefined}
                            onChange={date => handleFilterChange('requestedDate', date ? date.toISOString().split('T')[0] : undefined)}
                          />
                        </FormItem>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Form>
          
          {/* Message Requests Section */}
          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>Message Requests ({filteredRequests.length})</h3>
            </div>
            
            {/* Message Requests Table */}
            <Table hover responsive className="legacy-row-height-table">
              <thead>
                <tr>
                  <th>Priority</th>
                  <th>Client</th>
                  <th>Location</th>
                  <th>Message</th>
                  <th>Duration</th>
                  <th>Requested</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((request) => (
                  <tr key={request.id}>
                    <td>{getPriorityBadge(request.priority)}</td>
                    <td>{request.clientName}</td>
                    <td>{request.locationName}</td>
                    <td className={styles.messageText}>
                      <div className={styles.messagePreview}>
                        {request.messageText.length > 50 
                          ? `${request.messageText.substring(0, 50)}...` 
                          : request.messageText
                        }
                      </div>
                    </td>
                    <td>{formatDuration(request.duration)}</td>
                    <td>{request.requestedAt}</td>
                    <td>{getStatusBadge(request.status)}</td>
                    <td>
                      <div className={styles.actionButtons}>
                        <Button 
                          variant="link" 
                          className={styles.actionLink}
                          onClick={() => handleViewDetails(request)}
                        >
                          <EyeOutlined /> View
                        </Button>
                        {request.status === 'pending' && (
                          <>
                            <Button 
                              variant="link" 
                              className={styles.approveLink}
                              onClick={() => handleApprove(request.id)}
                            >
                              <CheckOutlined /> Approve
                            </Button>
                            <Button 
                              variant="link" 
                              className={styles.rejectLink}
                              onClick={() => handleReject(request.id)}
                            >
                              <CloseOutlined /> Reject
                            </Button>
                          </>
                        )}
                        {request.status === 'approved' && (
                          <Button 
                            variant="link" 
                            className={styles.scheduleLink}
                            onClick={() => handleSchedule(request.id)}
                          >
                            <PlayCircleOutlined /> Schedule
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          
          <PaginationRow
            page={page}
            onPageChange={(p) => setPage(p)}
            totalPages={typeof totalPages === "number" ? totalPages : undefined}
            onClickPrevious={handleOnClickPrev}
            handleOnClickNext={handleOnClickNext}
            disableLeft={page === 1}
            disableNext={page === totalPages}
          />
        </Card.Body>
      </Card>

      {/* Message Request Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Message Request Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Client:</strong> {selectedRequest.clientName}
                </Col>
                <Col md={6}>
                  <strong>Location:</strong> {selectedRequest.locationName}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Priority:</strong> {getPriorityBadge(selectedRequest.priority)}
                </Col>
                <Col md={6}>
                  <strong>Status:</strong> {getStatusBadge(selectedRequest.status)}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Requested By:</strong> {selectedRequest.requestedBy}
                </Col>
                <Col md={6}>
                  <strong>Requested At:</strong> {selectedRequest.requestedAt}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Duration:</strong> {formatDuration(selectedRequest.duration)}
                </Col>
                <Col md={6}>
                  <strong>Audio File:</strong> {selectedRequest.audioFile || 'Not uploaded'}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={12}>
                  <strong>Message Text:</strong>
                  <div className={styles.messageTextFull}>
                    "{selectedRequest.messageText}"
                  </div>
                </Col>
              </Row>
              {selectedRequest.notes && (
                <Row className="mb-3">
                  <Col md={12}>
                    <strong>Notes:</strong>
                    <p>{selectedRequest.notes}</p>
                  </Col>
                </Row>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
          {selectedRequest?.status === 'pending' && (
            <>
              <Button 
                variant="success" 
                onClick={() => selectedRequest && handleApprove(selectedRequest.id)}
              >
                <CheckOutlined /> Approve
              </Button>
              <Button 
                variant="danger" 
                onClick={() => selectedRequest && handleReject(selectedRequest.id)}
              >
                <CloseOutlined /> Reject
              </Button>
            </>
          )}
          {selectedRequest?.status === 'approved' && (
            <Button 
              variant="primary" 
              onClick={() => selectedRequest && handleSchedule(selectedRequest.id)}
            >
              <PlayCircleOutlined /> Schedule
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default withAuthenticationRequired(MessageRequests);
